import type { FloorBouquetInStock, FloorSellableItem } from "../../api/shopMode";
import type { CustomerOrder } from "../../api/types";
import { lineCents } from "../../utils/paymentSplit";
import { bouquetFloor, type BouquetFloorLine } from "./kioskCostDisplay";

/**
 * G-147 (OD-226): the shelf bouquets "+ Add items" offers. Owner: a bouquet
 * held by an order — this one's are already its goods — or by a web order is
 * not shown at all; one already added is not offered twice.
 */
export function offeredShelfBouquets(
  bouquets: FloorBouquetInStock[],
  alreadyAdded: Set<string>,
): FloorBouquetInStock[] {
  return bouquets.filter(
    (bouquet) =>
      bouquet.forOrderCode == null && !bouquet.forShopify && !alreadyAdded.has(bouquet.id),
  );
}

/**
 * The stock left for add-ons: the order's own shop items leave in the same
 * sale, so they are taken off what the shop holds before anything is added.
 */
export function stockLeftForExtras(
  items: FloorSellableItem[],
  order: Pick<CustomerOrder, "lines">,
): FloorSellableItem[] {
  const ordered = new Map<string, number>();
  for (const line of order.lines) {
    if (line.role !== "RETAIL_ITEM" || !line.itemId) continue;
    ordered.set(line.itemId, (ordered.get(line.itemId) ?? 0) + Number(line.quantity));
  }
  return items.flatMap((item) => {
    const left = Number(item.qtyOnHand) - (ordered.get(item.id) ?? 0);
    return left > 0 ? [{ ...item, qtyOnHand: String(left) }] : [];
  });
}

/**
 * G-177 (KI-132): the order's OWN bouquets at "Customer collected", priced as
 * the handover's sale will price them, with the floor the shelf list carries
 * for each — so the counter warns before the sale exactly as the till does.
 *
 * The prices follow the server's `buildSaleLinesFromOrder` (customer-orders
 * service), for this warning only; what the sale actually flags comes back
 * from the server after it and is the one the florist is asked about:
 *  - a ready-made bouquet the order holds sells at its agreed line price;
 *  - the bouquet built for the order sells at the charged total less the
 *    other goods at their agreed prices when the price was changed at the
 *    counter (OD-32), else at its agreed bouquet price — or the agreed total
 *    when the order has no other goods.
 * A bouquet the shelf list does not carry, or with no price, warns nothing.
 */
export function orderBouquetFloorLines(
  order: Pick<CustomerOrder, "lines" | "bouquetBuild" | "bouquetPriceEur" | "totalEur">,
  /** The total charged at the counter, when it differs from the agreed one. */
  chargedTotalEur: number | null,
  shelf: readonly FloorBouquetInStock[],
): BouquetFloorLine[] {
  const byId = new Map(shelf.map((bouquet) => [bouquet.id, bouquet]));
  const floorOf = (buildId: string) => {
    const bouquet = byId.get(buildId);
    return bouquet ? bouquetFloor(bouquet) : ({ kind: "none" } as const);
  };
  const out: BouquetFloorLine[] = [];
  const otherGoods = order.lines.filter(
    (line) => line.role === "RETAIL_ITEM" || line.role === "READY_BOUQUET",
  );
  for (const line of order.lines) {
    if (line.role !== "READY_BOUQUET" || !line.bouquetBuild || line.unitPriceEur == null) continue;
    out.push({
      label: line.bouquetBuild.name ?? line.description,
      price: Number(line.unitPriceEur),
      floor: floorOf(line.bouquetBuild.id),
    });
  }
  if (order.bouquetBuild) {
    let priceCents: number | null;
    if (chargedTotalEur != null) {
      const otherCents = otherGoods.reduce(
        (sum, line) => sum + lineCents(Number(line.unitPriceEur ?? 0), Number(line.quantity)),
        0,
      );
      priceCents = lineCents(chargedTotalEur) - otherCents;
    } else if (order.bouquetPriceEur != null) {
      priceCents = lineCents(Number(order.bouquetPriceEur));
    } else {
      priceCents =
        otherGoods.length === 0 && order.totalEur != null ? lineCents(Number(order.totalEur)) : null;
    }
    if (priceCents != null && priceCents >= 0) {
      out.push({
        label: order.bouquetBuild.name ?? "",
        price: priceCents / 100,
        floor: floorOf(order.bouquetBuild.id),
      });
    }
  }
  return out;
}
